import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import classnames from 'classnames';
import Dialog from '@material-ui/core/Dialog';
import {ClosableDialogTitle} from '@components/dialog/closable-title/ClosableDialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Loading from 'components/pure/utils/Loading';
import 'webdatarocks/webdatarocks.min.css';
import './WebDataRocksOverlay.sass';
import Button from '@material-ui/core/Button';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import WebDataRocksStore, {getWDRHeight} from '@store/web-data-rocks/WebDataRocksStore';
import WebDataDoesNotRock from 'components/dashboard/WebDataDoesNotRock';
import {Moment} from 'moment';
import 'flexmonster/flexmonster.css';
import WebDataRocksActions from '@store/web-data-rocks/WebDataRocksActions';
import AuthStore from 'stores/AuthStore';

interface WebDataRocksOverlayProps {
  startDate: Moment;
  endDate: Moment;
}

/**
 * WebDataRocks API Reference: https://www.webdatarocks.com/doc/api-list/
 */
@connectToStores
class WebDataRocksOverlay extends React.PureComponent<
  WebDataRocksOverlayProps & ReturnType<typeof WebDataRocksOverlay.getPropsFromStores>
> {
  static getStores() {
    return [WebDataRocksStore, AuthStore];
  }

  static getPropsFromStores() {
    const {hidden, dataLoaded, settingsLoaded, title, suggestedRanges, loadingError} =
      WebDataRocksStore.getState();
    const {user} = AuthStore.getState();

    return {
      hidden,
      dataLoaded,
      title,
      settingsLoaded,
      suggestedRanges,
      loadingError,
      user,
    };
  }

  componentDidMount() {
    if (this.props.user.isManager()) {
      WebDataRocksActions.getLicenseKey.defer();
    }
  }

  render() {
    const {
      hidden,
      dataLoaded,
      title,
      settingsLoaded,
      suggestedRanges,
      startDate,
      endDate,
      loadingError,
    } = this.props;
    const loading = !loadingError && (!dataLoaded || !settingsLoaded);
    const sizeError = !loading && !!suggestedRanges;

    return (
      <Dialog open={!hidden} className="wdr-overlay" maxWidth="lg">
        <ClosableDialogTitle onClose={WebDataRocksActions.hideDialog}>{title}</ClosableDialogTitle>
        <DialogContent>
          {loading && <Loading />}
          {loadingError && (
            <p className="loading-error">Failed to export. An unexpected error occurred.</p>
          )}
          {sizeError && (
            <WebDataDoesNotRock
              suggestedRanges={suggestedRanges}
              startDate={startDate}
              endDate={endDate}
            />
          )}
          <div
            className={classnames('wdr-wrapper', {
              hidden: loading || loadingError || sizeError,
              // below this height their internal modalsizeErrors
              // start to get cut off so we have to resize them ourselves.
              'scale-down': getWDRHeight() < 600,
            })}>
            <div id="wdr-root" />
          </div>
        </DialogContent>
        <DialogActions>
          {sizeError ? (
            <Button
              variant="contained"
              color="primary"
              className="ev-button"
              onClick={WebDataRocksActions.hideDialog}>
              Close
            </Button>
          ) : (
            <>
              <GenericTooltip tooltip={TOOLTIP}>
                <span className="generic-link">Help</span>
              </GenericTooltip>
              <Button
                variant="outlined"
                className="ev-button"
                onClick={WebDataRocksActions.resetToDefault}>
                Reset to default
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

const TOOLTIP = (
  <div className="pivot-tooltip">
    <h4>Pivoting & Exporting Data </h4>
    <p>
      To pivot data, click on Options and choose "Compact Form" as the layout. Now click on Fields
      and drag fields into either rows or columns to set up your pivot report. This can be exported
      straight to a PDF as you have it configured by clicking on Export. Any pivots that you set up
      will be automatically stored as preferences, so when you load the report next the same
      settings will be loaded. If you wish to clear them and view raw data again, click "Reset to
      Default".
    </p>
    <p>
      To export data, click on Export, and choose the format to export as. Note that if you want to
      export the raw data, click on "Reset to Default" first; this will remove any pivots that you
      have set up.
    </p>
  </div>
);

export default WebDataRocksOverlay as unknown as React.ComponentType<WebDataRocksOverlayProps>;
